import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import Plot from 'react-plotly.js';
import {
  ManRadioButtonGroup,
  ManRadioButton,
  ManText
} from '@craft-design-system/core-components-react';
import Shimmer_graph from '../shimmer_graph';
import { useFilterContext } from '../FilterContext';
import { useResetContext } from '../ResetContext';
import useGraphRadio from '../../utils/useGraphRadio';
import Zoom from '../Zoom';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import AverageDisplay from '../ride_analysis_kpi/Average_value';
import DownloadButtonComponent from '../DownloadButtonComponent';
import SegmentedControl from '../ride_analysis_kpi/SegmentedControl';
import AIToggleButton from '../AIToggleButton';
import ifav from './ifav.png';

const Charging_count = ({ value }) => {
  const [charging_count, set_charging_count] = useState(null);
  const [downloadData, setDownloadData] = useState([]);
  const [radio_buttons_test, set_Radio_values] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [chartType, setChartType] = useState("Bar");
  const { filterdata } = useFilterContext();
  const { startDate, endDate } = filterdata || {};
  const { resetdata } = useResetContext();
  const radio_query = useGraphRadio();
  const { fetchSegmentedChart } = useSegmentedChartQuery();
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [avg_value, setavg_value] = useState(value?.chargingcyclecount?.[2]?.average || 0);
  
  // 🔥 AI-related state
  const [cleanedAIData, setCleanedAIData] = useState([]);
  const [radioButtons, setRadioButtons] = useState({
    'Charging Count': 'Daily'
  });

  // 🔥 Clean download data for AI insights
  useEffect(() => {
    if (downloadData && downloadData.length > 0) {
      const cleanData = downloadData.filter(item => 
        item && typeof item === 'object' && Object.keys(item).length > 0
      );
      setCleanedAIData(cleanData);
    }
  }, [downloadData]);

  const popover = (
    <Popover id="popover-basic">
      <Popover.Body>
        <strong>Definition:</strong><br />
        This KPI displays the number of charging cycles.<br /><br />
  
        <strong>Telematic raw data for this KPI:</strong><br />
        TBM3:<br />
        • highvoltsysmode<br />
        • batterycurrent <br />
        • energylevel    <br />
        CM4:<br />
        • highvoltsysmode<br />
        • batterycurrent <br />
        • batcurrcbp     <br />
        • energylevel    <br />
        • energylevelcbp <br /><br />
  
        <strong>Time series data:</strong><br />
        If multiple vehicles are selected, average values are displayed. Days without data are not taken into account for the calculation.<br /><br />
  
        <strong>Histogram:</strong><br />
        Each bar represents the number of vehicles that fall within a certain range (bin) of <strong>monthly charging cycle values.</strong><br />
        All monthly values since start of data acquisition for the selected vehicle(s) are visualized per default.<br /><br />
  
        <strong>Boxplot:</strong><br />
        The boxplot shows the <strong>distribution of monthly charging cycle values</strong> for the selected vehicle(s) since start of data acquisition.<br />
        If multiple vehicles are selected, the boxplot reflects the combined distribution of all monthly values.<br />
        • The <strong>box</strong> represents the middle 50% of values (from the 25th percentile ("q1") to the 75th percentile ("q3")).<br />
        • The line <strong>inside the box</strong> shows the median (50th percentile).<br />
        • The whiskers extend to show the range of typical values ("min" and "max") and visualize <strong>the spread of the data</strong> beyond the central box.
      </Popover.Body>
    </Popover>
  );

  const handleSelectChange1 = (event) => {
    console.log('📻 Radio button changed:', event.detail.name, event.detail.value);
    
    const newRadioValues = {
      ...radio_buttons_test,
      [event.detail.name]: event.detail.value,
    };
    set_Radio_values(newRadioValues);
    
    // 🔥 Update radio buttons state for AI insights
    setRadioButtons(newRadioValues);
  };

  // 🔥 Fetch data when chart type changes
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        console.log('📊 Fetching segmented chart with category:', resetdata);
        
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          category: resetdata,
          columnname: "Charging Count",
        });

        if (chartType === 'Bar') {
          set_charging_count(JSON.parse(data[0]));
          setDownloadData(JSON.parse(data[3]));
        } else {
          set_charging_count(JSON.parse(data[0]));
          setDownloadData(JSON.parse(data[1]));
        }
      } catch (error) {
        console.error('Error fetching chart data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad && chartType && value?.vin_short) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType, resetdata]);

  // 🔥 Handle radio button changes - enhanced like battery temp components
  useEffect(() => {
    if (!radio_buttons_test) return;

    console.log('📻 Radio fetch triggered with:', radio_buttons_test);
    console.log('📻 Current category (resetdata):', resetdata);

    setIsLoading(true);

    // 🔥 Enhanced params to include category properly
    const params = {
      vins_short: [value.vin_short],
      radio_value: radio_buttons_test,
      columnname: 'Charging Count',
      startDate,
      endDate,
      filterdata: {
        ...filterdata,
        category: resetdata,
        resetdata: resetdata
      },
      resetdata: resetdata,
      category: resetdata,
      chartType,
    };

    console.log('📻 Sending radio query with params:', params);

    radio_query(params)
      .then(res => {
        console.log('📻 Radio query response:', res);

        // 🔥 Support BOTH response formats
        const payload = res.jsonData ?? res;
        
        if (payload) {
          set_charging_count(JSON.parse(payload[0] || '{}'));
          setavg_value(payload[2]?.average || 0);
          setDownloadData(JSON.parse(payload[3] || '[]'));
        }
      })
      .catch(err => {
        console.error("❌ Radio fetch failed:", err);
      })
      .finally(() => setIsLoading(false));

  }, [radio_buttons_test, resetdata, chartType]);

  // 🔥 Initialize data from props
  useEffect(() => {
    try {
      set_charging_count(JSON.parse(value?.chargingcyclecount?.[0] || '{}'));
      if (value?.chargingcyclecount?.[3]) {
        setDownloadData(JSON.parse(value.chargingcyclecount[3]));
      }
    } catch (error) {
      console.error("Error parsing JSON", error);
    }
  }, [value]);

  // 🔥 Monitor resetdata changes
  useEffect(() => {
    console.log('🔄 resetdata changed to:', resetdata);
    
    // If we have radio buttons selected and resetdata changes, refetch
    if (radio_buttons_test && resetdata) {
      console.log('🔄 Refetching due to category change');
      // Trigger refetch by updating radio buttons state slightly
      set_Radio_values({...radio_buttons_test});
    }
  }, [resetdata]);

  return (
    <div style={{ padding: '16px' }}>
      {/* Debug info - remove in production */}
      {/* <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
        Debug: Category = {resetdata || 'None'}, Radio = {JSON.stringify(radio_buttons_test)}
      </div> */}

      {/* Title + Info */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        gap: '8px'
      }}>
        <div style={{
          flex: 1,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: '130px'
        }}>
          <ManText
            className="card-title-text"
            variant="component-l"
            as="span"
            size="l"
            style={{ lineHeight: 'normal' }}
          >
            Charging cycle count
          </ManText>
        </div>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          flexDirection: "row-reverse",
          gap: 10 // 🔥 Added gap for consistent spacing
        }}>
          <OverlayTrigger trigger="hover" placement="left" overlay={popover}>
            <img 
              className="man-logovv1" 
              src={ifav} 
              alt="info" 
              style={{ 
                cursor: 'pointer',
                alignSelf: 'center' // 🔥 Ensures info icon aligns with other icons
              }} 
            />
          </OverlayTrigger>
          <Zoom
            chartData={charging_count?.data}
            chartLayout={charging_count?.layout}
            chartTitle="Charging cycle count"
          />
          <DownloadButtonComponent
            data={downloadData}
            fileName={`charging_count_${chartType.toLowerCase()}_${radio_buttons_test?.['Charging Count']?.toLowerCase() || 'daily'}.xlsx`}
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px', marginTop: '16px' }}>
        {chartType === 'Bar' ? (
          <div style={{ flexShrink: 0 }}>
            <AverageDisplay averages={avg_value} unit="cycles" />
          </div>
        ) : (
          <div style={{ width: '150px' }} />
        )}
        <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
          {chartType === "Bar" ? (
            <ManRadioButtonGroup
              name="Charging Count"
              invalid="false"
              onManChange={handleSelectChange1}
              style={{ display: 'flex', gap: '20px' }}
            >
              <ManRadioButton name="Charging Count" label="Daily" value="Daily" checked />
              <ManRadioButton name="Charging Count" label="Monthly" value="Monthly" />
              <ManRadioButton name="Charging Count" label="Yearly" value="Yearly" />
            </ManRadioButtonGroup>
          ) : (
            <div style={{ flexGrow: 1 }} />
          )}
        </div>

        <div style={{ marginLeft: 'auto' }}>
          <SegmentedControl
            selected={chartType}
            onChange={(type) => setChartType(type)}
            options={["Bar", "Histogram", "Boxplot"]}
          />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
  {isLoading ? (
    <Shimmer_graph />
  ) : (
    charging_count && (
      <>
        <Plot
          data={charging_count.data}
          layout={{
            ...charging_count.layout,
            autosize: true,
            responsive: true,
            margin: chartType === 'Bar' 
              ? { t: 10, r: 10, l: 10, b: 130 } // 🔥 Reduced margin since button is moved outside
              : { t: 10, r: 10, l: 10, b: 160 },
            height: chartType === 'Bar' ? 450 : 400,
            xaxis: {
              ...charging_count.layout?.xaxis,
              rangeslider: { visible: false },
            },
          }}
          useResizeHandler={true}
          style={{ 
            width: '100%', 
            height: chartType === 'Bar' ? '450px' : '400px'
          }}
        />

        {/* 🔥 AI Toggle Button - Bottom left corner */}
        {chartType === 'Bar' && cleanedAIData?.length > 0 && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-start', // 🔥 Left alignment
            marginTop: '16px' // 🔥 Space from chart
          }}>
            <AIToggleButton
              downloadData={cleanedAIData}
              radioButtonState={radioButtons}
              kpiName="Charging Count"
              chartType={chartType}
              vinShort={value.vin_short}
              title="AI | Automotive Intelligence - Charging Cycle Count Analysis"
            />
          </div>
        )}
      </>
    )
  )}
</div>
    </div>
  );
};

Charging_count.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.string.isRequired,
    chargingcyclecount: PropTypes.array.isRequired,
  }).isRequired,
};

export default Charging_count;